import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandMark } from "@/components/Nav";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = loc.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      nav(from, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 relative">
      <div className="hs-noise" />
      {/* Left — brand column */}
      <div
        className="relative hidden md:flex flex-col justify-between p-12 border-r border-hs overflow-hidden"
        style={{ background: "var(--hs-surface)" }}
      >
        <Link to="/" className="flex items-center gap-3 relative z-10" data-testid="login-brand">
          <BrandMark />
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-hs pl-3 border-l border-hs">
            Development Review
          </span>
        </Link>

        <div className="absolute inset-0 hs-grid-bg opacity-30" />

        <div className="relative z-10">
          <div className="hs-chip mb-8"><span className="dot" /> AUTH · SECURE</div>
          <h2 className="font-display text-5xl leading-[0.98] mb-6">
            Sign in to the <span className="text-warm italic">portal.</span>
          </h2>
          <p className="text-muted-hs max-w-md leading-relaxed">
            One authenticated door for CORE Suite, HT Design Lab, and the third application in flight. No context switching.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-6 border-t border-hs pt-8">
            {["CORE_SUITE", "HT_DESIGN_LAB", "APP_III"].map((n, i) => (
              <div key={n}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-warm mb-2">0{i + 1}</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-hs">{n}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-hs">
          [ FIG_LOGIN · TB BASIS · v1.0 ]
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex flex-col justify-between p-8 md:p-16" style={{ background: "var(--hs-bg)" }}>
        <div className="flex justify-between items-center">
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link inline-flex items-center gap-2" data-testid="login-back-home">
            <ArrowLeft size={14} /> Back
          </Link>
          <Link to="/register" className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link" data-testid="login-link-register">
            Need access? →
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md w-full mx-auto"
        >
          <span className="hs-chip mb-8"><span className="dot" /> STEP 01 · IDENTIFY</span>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-3">Welcome back.</h1>
          <p className="text-muted-hs mb-12">Sign in with your Harsight portal credentials.</p>

          <form onSubmit={submit} className="space-y-8" data-testid="login-form">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">Work email</span>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="hs-input"
                data-testid="login-input-email"
                autoComplete="email"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">Password</span>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="hs-input"
                data-testid="login-input-password"
                autoComplete="current-password"
              />
            </label>

            {error && (
              <div className="font-mono text-xs uppercase tracking-[0.15em] text-warm border border-warm/40 px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="hs-btn hs-btn-primary w-full justify-center"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
            </button>

            <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
              <Link to="/forgot-password" className="text-muted-hs hs-link" data-testid="login-link-forgot">
                Forgot password
              </Link>
              <Link to="/register" className="text-muted-hs hs-link">
                Create account
              </Link>
            </div>
          </form>
        </motion.div>

        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">
          © {new Date().getFullYear()} Harsight · Portal
        </div>
      </div>
    </div>
  );
}
